import React, { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Dialog as MuiDialog,
  useTheme,
} from '@mui/material';
import type { DialogProps as MuiDialogProps } from '@mui/material';
import ActionButton from '../ui/ActionButton';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  titleAction?: React.ReactNode;
  children: React.ReactNode;
  sidePanelContent?: React.ReactNode;
  defaultSidePanelOpen?: boolean;
  submitFormId?: string;
  onSubmitClick?: () => void;
  isSubmitting?: boolean;
  submitDisabled?: boolean;
  submitText?: React.ReactNode;
  cancelText?: React.ReactNode;
  hideSubmit?: boolean;
  hideCancel?: boolean;
  onCancelClick?: () => void;
  maxWidth?: MuiDialogProps['maxWidth'];
  disablePortal?: boolean;
  actionsStart?: React.ReactNode;
}

const Dialog: React.FC<DialogProps> = ({
  open,
  onClose,
  title,
  titleAction,
  children,
  sidePanelContent,
  defaultSidePanelOpen,
  submitFormId,
  onSubmitClick,
  isSubmitting,
  submitDisabled = false,
  submitText = 'Save',
  cancelText = 'Cancel',
  hideSubmit = false,
  hideCancel = false,
  onCancelClick,
  maxWidth,
  disablePortal = false,
  actionsStart,
}) => {
  const theme = useTheme();
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setPanelOpen(!!defaultSidePanelOpen);
    }
  }, [open, defaultSidePanelOpen]);

  const mainContentWidth =
    typeof maxWidth === 'string'
      ? maxWidth === 'xs'
        ? Math.max(theme.breakpoints.values.xs, 444)
        : theme.breakpoints.values[maxWidth]
      : theme.breakpoints.values.sm;
  const sidePanelTotalWidth = 312;
  const sidePanelDividerGapX = 32;
  const sidePanelContentWidth = sidePanelTotalWidth - sidePanelDividerGapX;
  const dialogContentPaddingX = 48;
  const contentAreaWidth = panelOpen
    ? mainContentWidth + sidePanelTotalWidth
    : mainContentWidth;
  const dialogWidth = contentAreaWidth + dialogContentPaddingX;

  const handleClose = useCallback(() => {
    setPanelOpen(false);
    onClose();
  }, [onClose]);

  return (
    <MuiDialog
      open={open}
      onClose={handleClose}
      disablePortal={disablePortal}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: dialogWidth,
          maxHeight: 'calc(100vh - 64px)',
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.3s ease-in-out',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        {title}
        {titleAction != null || sidePanelContent != null ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {titleAction}
            {sidePanelContent != null ? (
              <ActionButton
                variant="outline"
                size="sm"
                onClick={() => setPanelOpen((prev) => !prev)}
                icon={<AddIcon />}
                aria-label={
                  panelOpen ? 'Hide more details' : 'Add more details'
                }
              >
                {panelOpen ? 'Fewer details' : 'More details'}
              </ActionButton>
            ) : null}
          </Box>
        ) : null}
      </DialogTitle>
      <DialogContent
        sx={{
          flex: '1 1 auto',
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              flex: panelOpen ? `0 0 ${mainContentWidth}px` : '1 1 auto',
              minWidth: 0,
              maxWidth: '100%',
            }}
          >
            {children}
          </Box>
          <Box
            sx={{
              overflow: 'hidden',
              width: panelOpen ? sidePanelTotalWidth : 0,
              transition: 'width 0.3s ease-in-out',
            }}
          >
            {panelOpen && sidePanelContent != null && (
              <Box
                sx={{
                  width: sidePanelTotalWidth,
                  minWidth: sidePanelTotalWidth,
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'stretch',
                }}
              >
                <Divider orientation="vertical" flexItem sx={{ mx: 1.99 }} />
                <Box
                  sx={{
                    width: sidePanelContentWidth,
                    mt: 1,
                  }}
                >
                  {sidePanelContent}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexShrink: 0 }}>
        {actionsStart != null ? (
          <Box sx={{ mr: 'auto' }}>{actionsStart}</Box>
        ) : null}
        {!hideCancel ? (
          <ActionButton
            variant="outline"
            onClick={onCancelClick ?? handleClose}
            disabled={Boolean(isSubmitting)}
          >
            {cancelText}
          </ActionButton>
        ) : null}
        {!hideSubmit ? (
          <ActionButton
            variant="primary"
            type={onSubmitClick ? 'button' : 'submit'}
            form={onSubmitClick ? undefined : submitFormId}
            onClick={onSubmitClick}
            loading={Boolean(isSubmitting)}
            disabled={submitDisabled}
          >
            {submitText}
          </ActionButton>
        ) : null}
      </DialogActions>
    </MuiDialog>
  );
};

export default Dialog;
